import {Logger} from '../utils/managers/logger';

export enum SessionDatabaseStore {
  UrlCache = 'urlCache',
}

export class SessionDatabase {
  // Singleton Instance
  private static _instance: SessionDatabase;

  // Private
  private logger: Logger;
  private _database: {
    [store: string]: {
      [key: string]: any;
    };
  } = {}; // SessionDatabaseStore -> Array of items (key/id -> value)

  static get instance(): SessionDatabase {
    if (!this._instance) {
      this._instance = new SessionDatabase();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('SessionDatabase');
  }

  public async count(store: SessionDatabaseStore): Promise<number> {
    return Object.keys(this._database[store]).length;
  }

  public async getAll<T>(store: SessionDatabaseStore): Promise<Array<T>> {
    return Object.values(this._database[store]);
  }

  public async getById<T>(store: SessionDatabaseStore, id: string): Promise<T | undefined> {
    if (!this._database[store]) {
      return undefined;
    }
    return this._database[store][id];
  }

  public async put<T>(store: SessionDatabaseStore, id: string, data: T): Promise<void> {
    this._database[store] = {
      ...this._database[store],
      [id]: data,
    };
  }

  /**
   * Remove an item from the cache by its stored ID.
   */
  public async removeById(store: SessionDatabaseStore, id: string): Promise<void> {
    console.debug('🔴 Removing from session database:', id);
    delete this._database[store][id];
  }

  /**
   * Remove all items from the store that match the given regex.
   *
   * @example
   * ```
   * // Removes any `/entities/{entityId}/users{?...}` items.
   * SessionDatabase.instance.removeByMatch(
   *  SessionDatabaseStore.UrlCache,
   *  /^\/entities\/.*\/users/i
   * );
   * ```
   */
  public async removeByMatch(store: SessionDatabaseStore, match: RegExp): Promise<void> {
    if (!this._database[store]) {
      return;
    }

    const keys = Object.keys(this._database[store]);

    for (const key of keys) {
      if (match.test(key)) {
        this.removeById(store, key);
      }
    }
  }

  public async clearAllData(): Promise<void> {
    this._database = {};
  }
}
